/**
 * SuperAdmin portal route prefixes mapped to the grant that opens them.
 *
 * The sidebar already hides what a sub-user cannot reach (Sidebar.jsx
 * carries `saSection` / `ownerOnly` per item), but hiding a link is not
 * access control: typing `/superadmin/reports` in the URL bar still
 * rendered the page — the caller only discovered the block when a data
 * call or an export came back 403. This map lets `AuthGuard` bounce the
 * navigation itself, so a sub-user lands on their dashboard with a clear
 * message instead of a half-broken screen.
 *
 * Keep the section keys in sync with `SA_SECTION_CATALOGUE`
 * (server/src/services/superAdminPermissionService.js) and with the
 * sidebar's `saSection` values — the backend re-checks every request
 * either way, this layer only decides what is worth rendering.
 *
 * `ownerOnly: true` means no grant can unlock it (owner accounts only) —
 * the same rule `requireOwner()` enforces on the server.
 */
export const SA_PATH_SECTION_MAP = [
  { prefix: '/superadmin/schools', section: 'schools' },
  { prefix: '/superadmin/demo-requests', section: 'demo-requests' },
  { prefix: '/superadmin/abandoned-onboarding', section: 'abandoned-onboarding' },
  { prefix: '/superadmin/my-referrals', section: 'my-referrals' },
  { prefix: '/superadmin/subscriptions', section: 'subscriptions' },
  { prefix: '/superadmin/invoices', section: 'invoices' },
  { prefix: '/superadmin/earning-analytics', section: 'earnings' },
  { prefix: '/superadmin/reports', section: 'reports' },
  { prefix: '/superadmin/themes', section: 'themes' },
  { prefix: '/superadmin/blogs', section: 'blogs' },
  { prefix: '/superadmin/app-issues', section: 'app-issues' },
  { prefix: '/superadmin/message-templates', section: 'message-templates' },
  { prefix: '/superadmin/manage-users', section: 'superadmin-users' },
  { prefix: '/superadmin/activity-monitor', section: 'activity-logs' },
  { prefix: '/superadmin/server-logs', section: 'server-logs' },
  { prefix: '/superadmin/encryption-keys', section: 'encryption-keys' },
  { prefix: '/superadmin/settings', section: 'business-profile' },
  { prefix: '/superadmin/app-secrets', section: 'app-secrets' },
  // Managing who else gets access is owner territory, never delegable.
  { prefix: '/superadmin/access-control', ownerOnly: true },
  { prefix: '/superadmin/permission-sets', ownerOnly: true },
  // Not under /superadmin/* — the Help Desk page is shared with schooladmins,
  // who reach their own support chat there. This rule is consulted ONLY for
  // superadmin sessions (see AuthGuard), so it gates the support side without
  // touching the school side.
  { prefix: '/helpdesk', section: 'helpdesk' },
];

/**
 * Resolve the access rule for a SuperAdmin path.
 * Returns `{ section }`, `{ ownerOnly: true }`, or null when the path
 * isn't a gated SuperAdmin page (e.g. /superadmin-dashboard itself,
 * which every active SuperAdmin may open — it renders only the widgets
 * their grants cover).
 */
export const matchSuperAdminPathRule = (pathname) => {
  if (!pathname) return null;
  const hit = SA_PATH_SECTION_MAP.find(
    (r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`) || pathname.startsWith(`${r.prefix}?`),
  );
  return hit || null;
};

/**
 * Decide against a `/api/super-admin/permissions/me` payload
 * (`{ isOwner, inactive, sections }`). Owners pass everything; an
 * inactive account passes nothing.
 */
export const saRuleAllows = (rule, perms) => {
  if (!rule) return true;
  if (!perms) return false;
  if (perms.inactive) return false;
  if (perms.isOwner) return true;
  if (rule.ownerOnly) return false;
  const grant = perms.sections?.[rule.section];
  return !!(grant?.view || grant?.manage);
};
